/**
 * HQLA — фильтрация свотчей по семейству и типу + описание оттенка.
 *
 * Опирается на два факта о теме:
 *  1. product-info.js заменяет <variant-selects> целиком при каждой смене
 *     варианта, поэтому классы на свотчах проставляются заново после подмены.
 *  2. У product-info есть addPreProcessCallback — он вызывается ДО вставки,
 *     что позволяет разметить свотчи без мигания.
 *
 * Имена классов темы намеренно не используются: свотчи ищутся как
 * radio-инпуты внутри нужного fieldset, значение берётся из input.value.
 */
class ShadeSelector extends HTMLElement {
  connectedCallback() {
    this.data = this.#readData();
    if (!this.data) return;

    this.optionName = this.dataset.optionName || 'Color';
    this.activeFamily = 'All';
    this.activeType = 'All';
    this.showTypes = this.dataset.showTypes === 'true';

    this.tabsFamily = this.querySelector('.shade-selector__tabs:not(.shade-selector__tabs--type)');
    this.tabsType = this.querySelector('.shade-selector__tabs--type');
    this.moreBtn = this.querySelector('.shade-selector__more');
    this.countEl = this.querySelector('.shade-selector__count');
    this.headingValue = this.querySelector('.shade-selector__heading-value');
    this.descEl = this.querySelector('.shade-selector__desc');
    this.descName = this.querySelector('.shade-selector__desc-name');
    this.descText = this.querySelector('.shade-selector__desc-text');

    this.#buildTabs();
    this.#bind();
    this.#sync();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  #readData() {
    try {
      return JSON.parse(document.getElementById('hqla-shade-data').textContent);
    } catch {
      console.warn('[shade-selector] справочник оттенков не найден');
      return null;
    }
  }

  /** product-info — общий предок; <variant-selects> внутри него подменяется */
  get productInfo() {
    return this.closest('product-info');
  }

  /**
   * Radio-инпуты опции оттенка.
   * Основной признак — data-option-name, он проставляется темой
   * в product-variant-options.liquid на каждом инпуте.
   */
  #inputs() {
    const root = this.productInfo || document;
    const name = this.optionName;
    let list = Array.from(
      root.querySelectorAll(
        `variant-selects input[type="radio"][data-option-name="${CSS.escape(name)}"]`
      )
    );
    if (list.length) return list;

    // HQ PDP sections render their own option markup — no <variant-selects>
    // wrapper — so match the attribute anywhere inside the root.
    list = Array.from(
      root.querySelectorAll(`input[type="radio"][data-option-name="${CSS.escape(name)}"]`)
    );
    if (list.length) return list;

    // запасной путь, если тема перестанет проставлять data-option-name
    for (const fs of root.querySelectorAll('variant-selects fieldset')) {
      const legend = (fs.querySelector('legend')?.textContent || '').split(':')[0].trim();
      if (legend.toLowerCase() === name.toLowerCase()) {
        return Array.from(fs.querySelectorAll('input[type="radio"]'));
      }
    }
    return [];
  }

  /**
   * Узел, который нужно спрятать целиком: поднимаемся вверх, пока предок
   * содержит только этот инпут. Так не остаётся пустых обёрток,
   * съедающих gap в сетке. Разметка swatch-input может быть любой.
   */
  #unit(input) {
    let node = input;
    let parent = input.parentElement;
    while (parent && parent.tagName !== 'FIELDSET') {
      if (parent.querySelectorAll('input[type="radio"]').length > 1) break;
      node = parent;
      parent = parent.parentElement;
    }
    return node;
  }

  #labelFor(input) {
    // Implicit label — the input is a child of it, so there is no id to match
    if (!input.id) return input.closest('label');
    const root = this.productInfo || document;
    return root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
  }

  /** метаданные оттенков, реально присутствующих у этого товара */
  #present() {
    return this.#inputs()
      .map((i) => this.data.shades[i.value])
      .filter(Boolean);
  }

  #buildTabs() {
    const present = this.#present();
    const total = present.length;

    // нечего фильтровать — прячем весь блок, но описание оставляем
    if (total < 2) {
      this.tabsFamily.hidden = true;
      if (this.moreBtn) this.moreBtn.hidden = true;
      if (this.tabsType) this.tabsType.hidden = true;
      this.countEl.hidden = true;
      return;
    }

    const countBy = (key, val) => present.filter((s) => s[key] === val).length;

    const render = (container, list, key, active, onPick) => {
      container.innerHTML = '';
      const avail = list.filter((v) => countBy(key, v) > 0);
      if (avail.length < 2) {
        container.hidden = true;
        return false;
      }
      container.hidden = false;
      [['All', total], ...avail.map((v) => [v, countBy(key, v)])].forEach(([label, n]) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'shade-selector__tab';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', String(label === active));
        b.innerHTML = `${label}<span class="shade-selector__n">${n}</span>`;
        b.addEventListener('click', () => onPick(label));
        container.appendChild(b);
      });
      return true;
    };

    if (this.headingValue) this.headingValue.textContent = this.activeFamily;

    const hasFamily = render(this.tabsFamily, this.data.families, 'family', this.activeFamily, (v) => {
      this.activeFamily = v;
      this.#buildTabs();
      this.#apply();
      this.#selectFirstVisible();
    });

    const hasType =
      this.showTypes && this.tabsType
        ? render(this.tabsType, this.data.types, 'type', this.activeType, (v) => {
            this.activeType = v;
            this.#buildTabs();
            this.#apply();
            this.#selectFirstVisible();
          })
        : false;

    if (this.moreBtn) this.moreBtn.hidden = !hasType;
    if (this.tabsType && (!hasType || this.moreBtn?.getAttribute('aria-expanded') !== 'true')) {
      this.tabsType.hidden = true;
    }

    this.countEl.hidden = !hasFamily && !hasType;
  }

  #bind() {
    this.moreBtn?.addEventListener('click', () => {
      const open = this.moreBtn.getAttribute('aria-expanded') === 'true';
      this.moreBtn.setAttribute('aria-expanded', String(!open));
      this.tabsType.hidden = open;
    });

    // разметка новых свотчей до вставки в DOM — без мигания
    const pi = this.productInfo;
    if (pi?.addPreProcessCallback) {
      pi.addPreProcessCallback(() => queueMicrotask(() => this.#sync()));
    }

    // штатное событие темы после смены варианта
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.#sync());
    }

    // MutationObserver намеренно не используем: в галерее до 120 элементов,
    // и наблюдение за поддеревом product-info уходило в петлю
    // (пересборка меняет DOM → наблюдатель срабатывает снова).
    // Штатных хуков темы достаточно.

    const scope = this.productInfo || document;
    scope.addEventListener('change', (e) => {
      if (e.target?.type === 'radio' && e.target.dataset.optionName) this.#describe();
    });
  }

  /** перерисовка после подмены DOM: фильтр + описание */
  #sync() {
    if (this.syncing) return;
    this.syncing = true;
    requestAnimationFrame(() => {
      this.#buildTabs();
      this.#apply();
      this.#describe();
      this.syncing = false;
    });
  }

  /**
   * После смены фильтра выбираем первый видимый оттенок,
   * если текущий не попал в выборку. Если попал — не трогаем,
   * иначе переключение на «All» сбрасывало бы выбор покупателя.
   *
   * click() вместо ручной установки checked: он поднимает те же события,
   * что и настоящий клик, поэтому тема штатно обновит цену, фото и URL.
   */
  #selectFirstVisible() {
    const inputs = this.#inputs();
    if (!inputs.length) return;

    const visible = inputs.filter(
      (i) => !this.#unit(i).classList.contains('shade-hidden') && !i.disabled
    );
    if (!visible.length) return;

    const checked = inputs.find((i) => i.checked);
    if (checked && visible.includes(checked)) return;

    visible[0].click();
  }

  #apply() {
    const inputs = this.#inputs();
    if (!inputs.length) return;

    let shown = 0;
    inputs.forEach((input) => {
      const meta = this.data.shades[input.value];
      const noFilter = this.activeFamily === 'All' && this.activeType === 'All';
      const ok = meta
        ? (this.activeFamily === 'All' || meta.family === this.activeFamily) &&
          (this.activeType === 'All' || meta.type === this.activeType)
        : noFilter; // нет данных об оттенке — показываем только когда фильтр снят

      const unit = this.#unit(input);
      unit.classList.toggle('shade-hidden', !ok);
      if (unit === input) this.#labelFor(input)?.classList.toggle('shade-hidden', !ok);
      if (ok) shown += 1;
    });

    const total = inputs.length;
    this.countEl.textContent =
      shown === total ? `${total} shades` : `${shown} of ${total} shades`;
  }

  #describe() {
    const sel = this.#inputs().find((i) => i.checked);
    const meta = sel && this.data.shades[sel.value];
    if (!meta || !meta.desc) {
      this.descEl.hidden = true;
      return;
    }
    this.descName.textContent = `${sel.value}: `;
    this.descText.textContent = meta.desc;
    this.descEl.hidden = false;
  }
}

if (!customElements.get('shade-selector')) {
  customElements.define('shade-selector', ShadeSelector);
}